import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import { getUnreadCount } from '../../services/notificationService';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isRTL = i18n?.language === 'ar';

  const loadUnreadCount = async () => {
    const { count } = await getUnreadCount();
    setUnreadCount(count);
  };

  useEffect(() => {
    if (user) {
      loadUnreadCount();
      // Refresh unread count every 30 seconds
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in');
  };

  const mainNavItems = [
    { label: t('header.home'), path: '/' },
    { label: t('header.services'), path: '/services-overview' },
    { label: t('header.playerProfiles'), path: '/player-profiles', roles: ['scout', 'club', 'player', 'coach', 'admin'] },
    { label: t('header.successStories'), path: '/success-stories' },
    { label: t('header.contact'), path: '/contact' }
  ];

  const moreNavItems = [
    { label: t('header.clientPortal'), path: '/client-portal', roles: ['scout', 'club', 'player', 'coach', 'admin'] },
    { label: t('header.playerRegistration'), path: '/player-registration-form' },
    { label: t('header.adminDashboard'), path: '/admin-dashboard', roles: ['admin'] },
    { label: t('header.adminPanel'), path: '/admin-panel-dashboard', roles: ['admin'] },
    { label: t('header.performanceRecords'), path: '/performance-records-system', roles: ['admin'] },
    { label: t('header.marketIntelligence'), path: '/market-intelligence', roles: ['scout', 'club', 'admin'] },
    { label: t('header.partnershipNetwork'), path: '/partnership-network', roles: ['scout', 'club', 'admin'] },
    { label: t('header.complianceCenter'), path: '/compliance-center' },
    { label: t('header.unifiedDashboard'), path: '/unified-metrics-dashboard', roles: ['scout', 'club', 'admin'] },
    { label: t('header.performanceReports'), path: '/performance-reports', roles: ['scout', 'club', 'admin'] },
    { label: t('header.transferMarketplace'), path: '/live-transfer-marketplace', roles: ['scout', 'club', 'admin'] },
    { label: t('header.dealTracking'), path: '/deal-tracking-dashboard', roles: ['scout', 'club', 'admin'] },
    { label: t('header.marketValueAnalytics'), path: '/market-value-analytics', roles: ['scout', 'club', 'admin'] }
  ];

  const canAccessItem = (item) => {
    if (!item?.roles) return true;
    if (!user) return false;
    return item?.roles?.includes(userProfile?.role);
  };

  const filteredMainNavItems = mainNavItems?.filter(canAccessItem);
  const filteredMoreNavItems = moreNavItems?.filter(canAccessItem);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Trophy" size={24} color="white" />
            </div>
            <span className="text-xl font-bold text-foreground font-headline">ProX11Market</span>
          </div>

          <nav className="hidden lg:flex items-center gap-6">
            {filteredMainNavItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => navigate(item?.path)}
                className="text-sm font-medium text-foreground hover:text-primary transition-colors"
              >
                {item?.label}
              </button>
            ))}
            
            {filteredMoreNavItems?.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className="text-sm font-medium text-foreground hover:text-primary transition-colors flex items-center gap-1"
                >
                  More
                  <Icon name="ChevronDown" size={16} />
                </button>
                
                {moreMenuOpen && (
                  <div className={`absolute top-full mt-2 w-56 bg-card border border-border rounded-lg shadow-lg py-2 ${isRTL ? 'left-0' : 'right-0'}`}>
                    {filteredMoreNavItems?.map((item) => (
                      <button
                        key={item?.path}
                        onClick={() => {
                          navigate(item?.path);
                          setMoreMenuOpen(false);
                        }}
                        className="w-full text-start px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        {item?.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            {user ? (
              <>
                <button
                  onClick={() => navigate('/notification-center')}
                  className="relative p-2 hover:bg-muted rounded-lg transition-colors"
                  aria-label="Notifications"
                >
                  <Icon name="Bell" size={20} color="var(--color-foreground)" />
                  {unreadCount > 0 && (
                    <span className={`absolute -top-1 w-5 h-5 bg-destructive text-white text-xs font-bold rounded-full flex items-center justify-center ${isRTL ? '-left-1' : '-right-1'}`}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                  <Icon name="User" size={16} color="var(--color-primary)" />
                  <span className="text-sm font-medium text-foreground">
                    {userProfile?.full_name || user?.email}
                  </span>
                  {userProfile?.role && (
                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                      {userProfile?.role}
                    </span>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={handleSignOut}>
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => navigate('/sign-in')}>
                  {t('header.signIn')}
                </Button>
                <Button variant="default" size="sm" onClick={() => navigate('/sign-up')}>
                  {t('header.signUp')}
                </Button>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} color="var(--color-foreground)" />
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border">
            <nav className="flex flex-col gap-2">
              {filteredMainNavItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => {
                    navigate(item?.path);
                    setMobileMenuOpen(false);
                  }}
                  className="text-start px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                >
                  {item?.label}
                </button>
              ))}
              
              {filteredMoreNavItems?.length > 0 && (
                <>
                  <div className="border-t border-border my-2"></div>
                  <p className="px-4 py-1 text-xs font-semibold text-muted-foreground">More</p>
                  
                  {filteredMoreNavItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => {
                        navigate(item?.path);
                        setMobileMenuOpen(false);
                      }}
                      className="text-start px-4 py-2 text-sm text-foreground hover:bg-muted rounded-lg transition-colors"
                    >
                      {item?.label}
                    </button>
                  ))}
                </>
              )}
              
              <div className="border-t border-border my-2"></div>
              
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">{t('header.language')}</p>
                <LanguageSwitcher />
              </div>
              
              <div className="border-t border-border my-2"></div>
              
              {user ? (
                <>
                  <button
                    onClick={() => {
                      navigate('/notification-center');
                      setMobileMenuOpen(false);
                    }}
                    className="mx-4 flex items-center justify-between px-4 py-2 text-sm font-medium text-foreground hover:bg-muted rounded-lg transition-colors"
                  >
                    <span>Notifications</span>
                    {unreadCount > 0 && (
                      <span className="w-6 h-6 bg-destructive text-white text-xs font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <div className="px-4 py-2">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="User" size={16} color="var(--color-primary)" />
                      <span className="text-sm font-medium text-foreground">
                        {userProfile?.full_name || user?.email}
                      </span>
                    </div>
                    {userProfile?.role && (
                      <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                        {userProfile?.role}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="mx-4 px-4 py-2 text-sm font-medium text-foreground bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <div className="px-4 flex flex-col gap-2">
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => {
                      navigate('/sign-in');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    onClick={() => {
                      navigate('/sign-up');
                      setMobileMenuOpen(false);
                    }}
                  >
                    Sign Up
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;